<template>
  <div class="admin-layout" :class="{ 'sidebar-expanded': sidebarExpanded }">

    <!-- Mobile Top Bar -->
    <header class="mobile-topbar">
      <button class="mobile-menu-btn" @click="openMobileSidebar" aria-label="Open menu">
        <i class="fas fa-bars"></i>
      </button>
      <div class="mobile-topbar-right">
        <button class="mobile-icon-btn" @click="openImpersonationModal" aria-label="Login as user">
          <i class="fas fa-user-secret"></i>
        </button>
      </div>
    </header>

    <AdminSidebar
      ref="sidebarRef"
      @sidebar-change="onSidebarChange"
      @mobile-open-change="onMobileSidebarChange"
    />

    <div class="admin-main">
      <!-- Desktop Page Header -->
      <div class="page-header">
        <div class="page-header-left">
          <h1>Dashboard</h1>
          <p>Welcome back, <span class="username">{{ user?.first_name || user?.username }}</span></p>
        </div>
        <div class="page-actions">
          <button
            v-if="oljScrapeRunning"
            type="button"
            @click="cancelOljScrape"
            class="btn btn-danger"
            :disabled="scrapeRunning || cancelRunning"
          >
            <i class="fas fa-stop-circle"></i>
            <span>{{ cancelRunning ? 'Cancelling…' : 'Cancel Scrape' }}</span>
          </button>
          <button
            v-else
            type="button"
            @click="triggerOljScrape"
            class="btn btn-ghost"
            :disabled="scrapeRunning"
            :title="scrapeButtonTitle"
          >
            <i class="fas fa-robot"></i>
            <span>{{ scrapeRunning ? 'Starting…' : 'Get Latest Jobs' }}</span>
          </button>
          <button type="button" @click="openImpersonationModal" class="btn btn-primary">
            <i class="fas fa-user-secret"></i>
            <span>Login as User</span>
          </button>
        </div>
      </div>

      <!-- Mobile Page Title Bar -->
      <div class="mobile-page-header">
        <div>
          <h1>Dashboard</h1>
          <p>Welcome back, <span class="username">{{ user?.first_name || user?.username }}</span></p>
        </div>
        <div class="mobile-scrape-actions">
          <button
            v-if="oljScrapeRunning"
            type="button"
            @click="cancelOljScrape"
            class="btn btn-danger btn-sm"
            :disabled="scrapeRunning || cancelRunning"
          >
            <i class="fas fa-stop-circle"></i>
          </button>
          <button
            v-else
            type="button"
            @click="triggerOljScrape"
            class="btn btn-ghost btn-sm"
            :disabled="scrapeRunning"
          >
            <i class="fas fa-robot"></i>
          </button>
        </div>
      </div>

      <!-- Banners -->
      <div v-if="scrapeError" class="banner banner--error">
        <i class="fas fa-exclamation-circle"></i>
        {{ scrapeError }}
      </div>
      <div v-else-if="scrapeMessage" class="banner banner--success">
        <i class="fas fa-check-circle"></i>
        {{ scrapeMessage }}
      </div>

      <!-- Loading -->
      <div v-if="loading" class="state-center">
        <div class="spinner"></div>
        <p>Loading dashboard data…</p>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="state-center">
        <i class="fas fa-exclamation-triangle state-icon"></i>
        <h3>Error Loading Dashboard</h3>
        <p>{{ error }}</p>
        <button @click="loadDashboardData" class="btn btn-primary">Retry</button>
      </div>

      <!-- Content -->
      <div v-else class="content">

        <!-- Stats Grid -->
        <div class="stats-grid stats-grid--3">
          <div class="stat-card">
            <div class="stat-card-accent" style="background: #3b82f6;"></div>
            <div class="stat-card-inner">
              <div class="stat-top">
                <span class="stat-label">Total Users</span>
                <div class="stat-icon blue"><i class="fas fa-users"></i></div>
              </div>
              <div class="stat-value">{{ userMetrics.total_users?.toLocaleString() || '0' }}</div>
              <div class="stat-sub">
                <i class="fas fa-arrow-up trend-up"></i>
                +{{ userMetrics.user_growth_7_days || 0 }} this week
              </div>
            </div>
          </div>

          <div class="stat-card">
            <div class="stat-card-accent" style="background: #22c55e;"></div>
            <div class="stat-card-inner">
              <div class="stat-top">
                <span class="stat-label">Total Jobs</span>
                <div class="stat-icon green"><i class="fas fa-briefcase"></i></div>
              </div>
              <div class="stat-value">{{ jobMetrics.total_jobs?.toLocaleString() || '0' }}</div>
              <div class="stat-sub">
                <i class="fas fa-check-circle trend-ok"></i>
                {{ jobMetrics.approved_jobs || 0 }} approved
              </div>
            </div>
          </div>

          <div class="stat-card stat-card--clickable" @click="router.push('/admin-dashboard/support-tickets')">
            <div class="stat-card-accent" style="background: #ef4444;"></div>
            <div class="stat-card-inner">
              <div class="stat-top">
                <span class="stat-label">New Tickets Today</span>
                <div class="stat-icon red"><i class="fas fa-ticket-alt"></i></div>
              </div>
              <div class="stat-value">{{ ticketStats.today ?? '—' }}</div>
              <div class="stat-sub">
                <i class="fas fa-circle-dot trend-warn"></i>
                {{ ticketStats.open || 0 }} open tickets
              </div>
            </div>
          </div>
        </div>

        <!-- Subscription Stats Grid -->
        <div class="stats-grid">
          <div class="stat-card">
            <div class="stat-card-accent" style="background: #22c55e;"></div>
            <div class="stat-card-inner">
              <div class="stat-top">
                <span class="stat-label">Paid Subscribers</span>
                <div class="stat-icon green"><i class="fas fa-crown"></i></div>
              </div>
              <div class="stat-value">{{ subscriptionMetrics.paid_subscribers?.toLocaleString() || '0' }}</div>
              <div class="stat-sub">
                <i class="fas fa-check-circle trend-ok"></i>
                Active paid accounts
              </div>
            </div>
          </div>

          <div class="stat-card">
            <div class="stat-card-accent" style="background: #6366f1;"></div>
            <div class="stat-card-inner">
              <div class="stat-top">
                <span class="stat-label">MRR</span>
                <div class="stat-icon indigo"><i class="fas fa-peso-sign"></i></div>
              </div>
              <div class="stat-value">&#8369;{{ subscriptionMetrics.mrr?.toLocaleString() || '0' }}</div>
              <div class="stat-sub">
                <i class="fas fa-chart-line trend-ok"></i>
                Monthly recurring revenue
              </div>
            </div>
          </div>

          <div class="stat-card">
            <div class="stat-card-accent" style="background: #3b82f6;"></div>
            <div class="stat-card-inner">
              <div class="stat-top">
                <span class="stat-label">New This Month</span>
                <div class="stat-icon blue"><i class="fas fa-user-plus"></i></div>
              </div>
              <div class="stat-value">{{ subscriptionMetrics.new_paid_this_month?.toLocaleString() || '0' }}</div>
              <div class="stat-sub">
                <i class="fas fa-arrow-up trend-up"></i>
                New paid conversions
              </div>
            </div>
          </div>

          <div class="stat-card">
            <div class="stat-card-accent" style="background: #8b5cf6;"></div>
            <div class="stat-card-inner">
              <div class="stat-top">
                <span class="stat-label">Conversion Rate</span>
                <div class="stat-icon purple"><i class="fas fa-percent"></i></div>
              </div>
              <div class="stat-value">{{ subscriptionMetrics.conversion_rate || '0' }}%</div>
              <div class="stat-sub">
                <i class="fas fa-exchange-alt trend-ok"></i>
                Trial to paid rate
              </div>
            </div>
          </div>
        </div>

        <!-- Subscription Overview Card -->
        <div class="chart-card subscription-overview-card">
          <div class="subscription-overview-header">
            <div class="subscription-overview-title-wrap">
              <div class="subscription-overview-icon">
                <i class="fas fa-receipt"></i>
              </div>
              <h2 class="subscription-overview-title">Subscription Overview</h2>
            </div>
          </div>
          <div class="subscription-overview-body">
            <div class="sub-overview-grid">
              <div class="sub-overview-row">
                <span class="sub-overview-key">Total Revenue</span>
                <span class="sub-overview-val">&#8369;{{ subscriptionMetrics.total_revenue?.toLocaleString() || '0' }}</span>
              </div>
              <div class="sub-overview-row">
                <span class="sub-overview-key">MRR</span>
                <span class="sub-overview-val">&#8369;{{ subscriptionMetrics.mrr?.toLocaleString() || '0' }}</span>
              </div>
              <div class="sub-overview-row">
                <span class="sub-overview-key">Cancelled (last 30d)</span>
                <span class="sub-overview-val sub-overview-val--warn">{{ subscriptionMetrics.cancelled_last_30_days || '0' }}</span>
              </div>
              <div class="sub-overview-row">
                <span class="sub-overview-key">Paid Subscribers</span>
                <span class="sub-overview-val">{{ subscriptionMetrics.paid_subscribers?.toLocaleString() || '0' }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Charts Row -->
        <div class="chart-card">
          <UserRegistrationChart :chartData="userRegistrationData" />
        </div>
        <div class="chart-card">
          <JobPostsDistributionChart
            :chart-data="jobPostsDistributionData"
            :period="jobCategoriesPeriod"
            :loading="distributionLoading"
            @update:period="onJobCategoriesPeriodChange"
          />
        </div>
        <div class="chart-card">
          <JobApplicationsBySubscriptionChart />
        </div>

      </div>
    </div>

    <!-- Impersonation Modal -->
    <div
      v-if="showImpersonationModal"
      class="modal-overlay"
      @click.self="closeImpersonationModal"
    >
      <div class="modal">
        <div class="modal-header">
          <div class="modal-title">
            <div class="modal-title-icon">
              <i class="fas fa-user-secret"></i>
            </div>
            <div>
              <h3>Login as User</h3>
              <p>Select a user to access their account</p>
            </div>
          </div>
          <button type="button" class="modal-close" @click="closeImpersonationModal">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="modal-body">
          <div class="modal-filters-row">
            <div class="modal-search-wrap">
              <i class="fas fa-search modal-search-icon"></i>
              <input
                v-model="impersonationQuery"
                type="text"
                class="modal-search"
                placeholder="Search by name, username, or email…"
                @input="debouncedFetchImpersonationUsers"
              />
            </div>

            <div class="modal-date-filter">
              <label>
                Joined from
                <input
                  v-model="impersonationDateFrom"
                  type="date"
                  class="modal-date-input"
                  @change="fetchImpersonationUsers"
                />
              </label>
              <label>
                Joined to
                <input
                  v-model="impersonationDateTo"
                  type="date"
                  class="modal-date-input"
                  @change="fetchImpersonationUsers"
                />
              </label>
              <button
                v-if="impersonationDateFrom || impersonationDateTo"
                type="button"
                class="modal-date-clear"
                @click="clearImpersonationDateFilter"
              >
                Clear
              </button>
            </div>
          </div>

          <div v-if="impersonationLoading" class="modal-empty">
            <div class="spinner spinner--sm"></div>
            Loading users…
          </div>

          <div v-else class="modal-list">
            <button
              v-for="account in impersonationUsers"
              :key="account.id"
              type="button"
              class="modal-user-row"
              :class="{ 'modal-user-row--disabled': !canImpersonateAccount(account) }"
              :disabled="impersonationSubmitting || !canImpersonateAccount(account)"
              :title="!canImpersonateAccount(account) ? 'User hasn\'t finished onboarding yet' : null"
              @click="impersonateUser(account)"
            >
              <div class="modal-user-avatar">
                {{ (account.first_name?.[0] || account.username?.[0] || '?').toUpperCase() }}
              </div>
              <div class="modal-user-info">
                <p class="modal-user-name">
                  {{ account.first_name || account.last_name
                    ? `${account.first_name || ''} ${account.last_name || ''}`.trim()
                    : account.username }}
                </p>
                <p class="modal-user-meta">@{{ account.username }} · {{ account.email }}</p>
              </div>
              <span v-if="!canImpersonateAccount(account)" class="modal-user-badge modal-user-badge--warn">
                <i class="fas fa-lock"></i> not onboarded
              </span>
              <span v-else class="modal-user-badge">{{ account.account_type }}</span>
            </button>
            <p v-if="!impersonationUsers.length && !impersonationLoading" class="modal-empty">
              <i class="fas fa-user-slash"></i>
              No users found
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue'
import { useRouter } from 'vue-router'
import authStore from '@/store/auth'
import apiClient from '@/services/api'
import AdminSidebar from '@/components/admin/AdminSidebar.vue'
import UserRegistrationChart from '@/components/charts/UserRegistrationChart.vue'
import JobPostsDistributionChart from '@/components/charts/JobPostsDistributionChart.vue'
import JobApplicationsBySubscriptionChart from '@/components/charts/JobApplicationsBySubscriptionChart.vue'

export default {
  name: 'AdminDashboard',
  components: { AdminSidebar, UserRegistrationChart, JobPostsDistributionChart, JobApplicationsBySubscriptionChart },
  setup() {
    const router = useRouter()
    const loading = ref(true)
    const error = ref(null)
    const userMetrics = ref({})
    const jobMetrics = ref({})
    const subscriptionMetrics = ref({})
    const ticketStats = ref({})

    const sidebarRef = ref(null)
    const sidebarExpanded = ref(false)
    const onSidebarChange = (isExpanded) => { sidebarExpanded.value = isExpanded }
    // eslint-disable-next-line no-unused-vars
    const onMobileSidebarChange = (isOpen) => { /* can track mobile state if needed */ }

    function openMobileSidebar() {
      sidebarRef.value?.openMobile()
    }

    const scrapeRunning = ref(false)
    const cancelRunning = ref(false)
    const scrapeMessage = ref(null)
    const scrapeError = ref(null)
    const oljScrapeRunning = ref(false)

    const showImpersonationModal = ref(false)
    const impersonationUsers = ref([])
    const impersonationQuery = ref('')
    const impersonationDateFrom = ref('')
    const impersonationDateTo = ref('')
    const impersonationLoading = ref(false)
    const impersonationSubmitting = ref(false)
    let impersonationSearchTimer = null

    const user = computed(() => authStore.state.user)
    const scrapeButtonTitle = computed(() =>
      scrapeRunning.value ? 'Sending request…' : 'Queue full OnlineJobs.ph scrape (requires Celery worker)'
    )
    const isAdmin = computed(() =>
      user.value && (user.value.is_superuser || user.value.account_type === 'admin' || user.value.admin_profile)
    )

    if (!isAdmin.value) {
      router.push('/')
      return {
        loading: ref(false), error: ref('Access denied.'),
        userMetrics: ref({}), jobMetrics: ref({}),
        user: computed(() => null),
        loadDashboardData: () => {},
        userRegistrationData: ref({}), jobRegistrationData: ref({}),
        jobApplicationsData: ref({}), jobPostsDistributionData: ref({}),
      }
    }

    const userRegistrationData = ref({ labels: [], jobseekers: [], employers: [] })
    const jobPostsDistributionData = ref({ top_tags: { labels: [], counts: [], ids: [] } })
    const jobCategoriesPeriod = ref('month')
    const distributionLoading = ref(false)

    const applyJobPostsDistribution = (data) => {
      const tt = data?.top_tags || {}
      jobPostsDistributionData.value = {
        top_tags: { labels: tt.labels || [], counts: tt.counts || [], ids: tt.ids || [] },
      }
    }

    const loadJobPostsDistribution = async (showLoading = false) => {
      if (showLoading) distributionLoading.value = true
      try {
        const response = await apiClient.get('/admin-dashboard/job-posts-distribution/', {
          params: { period: jobCategoriesPeriod.value },
        })
        applyJobPostsDistribution(response.data)
      } catch (err) {
        console.error('Error loading job posts distribution:', err)
        throw err
      } finally {
        if (showLoading) distributionLoading.value = false
      }
    }

    const onJobCategoriesPeriodChange = async (next) => {
      const v = String(next || 'month').toLowerCase()
      const allowed = ['day', 'week', 'month']
      jobCategoriesPeriod.value = allowed.includes(v) ? v : 'month'
      try {
        await loadJobPostsDistribution(true)
      } catch {
        error.value = 'Failed to load category data.'
      }
    }

    const loadDashboardData = async () => {
      try {
        loading.value = true
        error.value = null
        const [userRes, jobRes, monthlyRes, distRes, subRes, ticketRes] = await Promise.all([
          apiClient.get('/admin-dashboard/user-metrics/'),
          apiClient.get('/admin-dashboard/job-metrics/'),
          apiClient.get('/admin-dashboard/user-registration-monthly/'),
          apiClient.get('/admin-dashboard/job-posts-distribution/', { params: { period: jobCategoriesPeriod.value } }),
          apiClient.get('/admin-dashboard/subscription-metrics/').catch((err) => {
            console.error('Subscription metrics load error:', err)
            return { data: {} }
          }),
          apiClient.get('/admin-dashboard/support-tickets/stats/').catch((err) => {
            console.error('Ticket stats load error:', err)
            return { data: {} }
          }),
        ])
        userMetrics.value = userRes.data
        jobMetrics.value = jobRes.data
        subscriptionMetrics.value = subRes.data || {}
        ticketStats.value = ticketRes.data || {}
        userRegistrationData.value = { labels: monthlyRes.data.labels, jobseekers: monthlyRes.data.jobseekers, employers: monthlyRes.data.employers }
        applyJobPostsDistribution(distRes.data)
      } catch (err) {
        console.error('Dashboard load error:', err)
        if (err.response?.status === 403) { error.value = 'Access denied.'; router.push('/') }
        else error.value = 'Failed to load dashboard data. Please try again.'
      } finally {
        loading.value = false
      }
    }

    const checkOljScrapeStatus = async () => {
      try {
        const res = await apiClient.get('/admin-dashboard/run-olj-scraper/status/')
        oljScrapeRunning.value = !!res?.data?.running
      } catch (err) { console.error(err) }
    }

    let scrapeStatusPollId = null
    const startScrapeStatusPolling = () => {
      if (scrapeStatusPollId != null) return
      scrapeStatusPollId = setInterval(() => { if (oljScrapeRunning.value) checkOljScrapeStatus() }, 15000)
    }

    const triggerOljScrape = async () => {
      if (scrapeRunning.value || oljScrapeRunning.value) return
      scrapeRunning.value = true; scrapeError.value = null; scrapeMessage.value = null
      try {
        const res = await apiClient.post('/admin-dashboard/run-olj-scraper/')
        scrapeMessage.value = res?.data?.message || 'OLJ scraping queued.'
        await checkOljScrapeStatus()
      } catch (err) {
        if (err.response?.status === 409) { scrapeMessage.value = err.response?.data?.error || 'Already running.'; await checkOljScrapeStatus() }
        else scrapeError.value = err.response?.data?.error || 'Failed to start scraper.'
      } finally { scrapeRunning.value = false }
    }

    const cancelOljScrape = async () => {
      if (cancelRunning.value || scrapeRunning.value) return
      cancelRunning.value = true; scrapeError.value = null
      try {
        await apiClient.post('/admin-dashboard/run-olj-scraper/cancel/')
        scrapeMessage.value = null; oljScrapeRunning.value = false
        await checkOljScrapeStatus()
      } catch (err) {
        scrapeError.value = err.response?.data?.error || 'Failed to cancel scrape.'
      } finally { cancelRunning.value = false }
    }

    const fetchImpersonationUsers = async () => {
      impersonationLoading.value = true
      try {
        const res = await apiClient.get('/admin-dashboard/users/', {
          params: {
            q: impersonationQuery.value || '',
            date_from: impersonationDateFrom.value || '',
            date_to: impersonationDateTo.value || '',
          }
        })
        impersonationUsers.value = res?.data?.results || []
      } catch (err) { console.error(err); impersonationUsers.value = [] }
      finally { impersonationLoading.value = false }
    }

    const debouncedFetchImpersonationUsers = () => {
      clearTimeout(impersonationSearchTimer)
      impersonationSearchTimer = setTimeout(fetchImpersonationUsers, 250)
    }

    const clearImpersonationDateFilter = () => {
      impersonationDateFrom.value = ''; impersonationDateTo.value = ''
      fetchImpersonationUsers()
    }

    const openImpersonationModal = async () => {
      showImpersonationModal.value = true; impersonationQuery.value = ''
      impersonationDateFrom.value = ''; impersonationDateTo.value = ''
      await fetchImpersonationUsers()
    }

    const closeImpersonationModal = () => {
      showImpersonationModal.value = false; impersonationQuery.value = ''; impersonationUsers.value = []
      impersonationDateFrom.value = ''; impersonationDateTo.value = ''
    }

    const canImpersonateAccount = (account) =>
      !!(account.onboarding_completed || account.impersonation_override)

    const impersonateUser = async (account) => {
      if (impersonationSubmitting.value || !canImpersonateAccount(account)) return
      impersonationSubmitting.value = true
      try {
        const currentToken = localStorage.getItem('token')
        const res = await apiClient.post('/admin-dashboard/impersonate/', { user_id: account.id })
        const targetToken = res?.data?.token
        const adminToken = res?.data?.admin_token || currentToken
        if (!targetToken || !adminToken) throw new Error('Missing impersonation token data.')
        localStorage.setItem('admin_token', adminToken)
        localStorage.setItem('admin_user_snapshot', JSON.stringify(authStore.state.user || null))
        localStorage.setItem('is_impersonating', 'true')
        localStorage.setItem('token', targetToken)
        apiClient.defaults.headers.common.Authorization = `Token ${targetToken}`
        await authStore.checkAuthStatus()
        closeImpersonationModal()
        router.push('/job-filters')
      } catch (err) {
        console.error(err); error.value = err.response?.data?.error || 'Failed to login as user.'
      } finally { impersonationSubmitting.value = false }
    }

    onMounted(() => { checkOljScrapeStatus(); loadDashboardData(); startScrapeStatusPolling() })
    onBeforeUnmount(() => {
      if (scrapeStatusPollId != null) { clearInterval(scrapeStatusPollId); scrapeStatusPollId = null }
      clearTimeout(impersonationSearchTimer)
    })

    return {
      loading, error, userMetrics, jobMetrics, subscriptionMetrics, ticketStats, user, router,
      loadDashboardData,
      userRegistrationData,
      jobPostsDistributionData, jobCategoriesPeriod, distributionLoading, onJobCategoriesPeriodChange,
      triggerOljScrape, cancelOljScrape, scrapeRunning, cancelRunning,
      oljScrapeRunning, scrapeMessage, scrapeError, scrapeButtonTitle,
      showImpersonationModal, impersonationUsers, impersonationQuery,
      impersonationDateFrom, impersonationDateTo, clearImpersonationDateFilter,
      fetchImpersonationUsers,
      impersonationLoading, impersonationSubmitting,
      openImpersonationModal, closeImpersonationModal,
      debouncedFetchImpersonationUsers, impersonateUser, canImpersonateAccount,
      sidebarExpanded, onSidebarChange, onMobileSidebarChange,
      sidebarRef, openMobileSidebar,
    }
  },
}
</script>

<style scoped>
/* ── Layout ── */
.admin-layout {
  display: flex;
  min-height: 100vh;
  background: hsl(var(--background));
}

.admin-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
}

.sidebar-expanded .admin-main {
  transition: none;
}

/* ── Mobile Top Bar ── */
.mobile-topbar {
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 56px;
  background: hsl(var(--card));
  border-bottom: 1px solid hsl(var(--border));
  z-index: 98;
  align-items: center;
  padding: 0 1rem;
  gap: 0.75rem;
  box-shadow: 0 1px 8px rgba(0,0,0,0.2);
}

.mobile-menu-btn {
  width: 36px;
  height: 36px;
  background: hsl(var(--muted));
  border: none;
  border-radius: 9px;
  color: hsl(var(--muted-foreground));
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, color 0.15s;
  flex-shrink: 0;
}

.mobile-menu-btn:hover { background: hsl(var(--accent)); color: hsl(var(--foreground)); }

.mobile-topbar-right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: auto;
}

.mobile-icon-btn {
  width: 36px;
  height: 36px;
  background: hsl(var(--muted));
  border: none;
  border-radius: 9px;
  color: hsl(var(--muted-foreground));
  font-size: 0.9rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, color 0.15s;
}

.mobile-icon-btn:hover { background: hsl(var(--accent)); color: hsl(var(--foreground)); }

/* ── Mobile Page Header ── */
.mobile-page-header { display: none; }

/* ── Desktop Page Header ── */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem 1.5rem 0.25rem;
}

.page-header h1 {
  font-size: 1.35rem;
  font-weight: 700;
  color: hsl(var(--foreground));
  margin: 0 0 2px;
  letter-spacing: -0.02em;
}

.page-header p {
  font-size: 0.8rem;
  color: hsl(var(--muted-foreground));
  margin: 0;
}

.username { color: hsl(var(--primary)); font-weight: 600; }

.page-actions {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-shrink: 0;
}

/* ── Buttons ── */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.45rem 1rem;
  border-radius: 8px;
  border: none;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, opacity 0.15s;
  white-space: nowrap;
}

.btn.btn-sm {
  padding: 0.4rem 0.65rem;
  font-size: 0.78rem;
}

.btn:disabled { opacity: 0.55; cursor: not-allowed; }

.btn-primary  { background: hsl(var(--primary)); color: hsl(var(--primary-foreground)); }
.btn-primary:hover:not(:disabled) { background: hsl(var(--primary) / 0.85); }

.btn-ghost { background: hsl(var(--card)); color: hsl(var(--foreground)); border: 1px solid hsl(var(--border)); }
.btn-ghost:hover:not(:disabled) { background: hsl(var(--muted)); }

.btn-danger { background: rgba(239, 68, 68, 0.15); color: #f87171; border: 1px solid rgba(239, 68, 68, 0.35); }
.btn-danger:hover:not(:disabled) { background: rgba(239, 68, 68, 0.25); }

/* ── Banners ── */
.banner {
  margin: 0.75rem 1.5rem 0;
  padding: 0.65rem 1rem;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.banner--success { background: rgba(34, 197, 94, 0.15); color: #4ade80; border: 1px solid rgba(34, 197, 94, 0.35); }
.banner--error   { background: rgba(239, 68, 68, 0.15); color: #f87171; border: 1px solid rgba(239, 68, 68, 0.35); }

/* ── States ── */
.state-center {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  color: hsl(var(--muted-foreground));
  gap: 0.75rem;
}

.state-icon { font-size: 2.5rem; color: #fbbf24; }

.spinner {
  width: 36px;
  height: 36px;
  border: 3px solid hsl(var(--border));
  border-top-color: hsl(var(--primary));
  border-radius: 50%;
  animation: spin 0.75s linear infinite;
}

.spinner--sm { width: 18px; height: 18px; border-width: 2px; }

@keyframes spin { to { transform: rotate(360deg); } }

/* ── Content ── */
.content {
  padding: 1rem 1.5rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

/* ── Stats ── */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.85rem;
}

.stats-grid--3 {
  grid-template-columns: repeat(3, 1fr);
}

.stat-card {
  background: hsl(var(--card));
  border: 1px solid hsl(var(--border));
  border-radius: 12px;
  overflow: hidden;
  transition: box-shadow 0.2s;
}

.stat-card:hover { box-shadow: 0 4px 20px rgba(0, 0, 0, 0.07); }
.stat-card-accent { height: 3px; width: 100%; }
.stat-card-inner { padding: 0.9rem 1rem 1rem; }

.stat-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.65rem;
}

.stat-label {
  font-size: 10px;
  font-weight: 700;
  color: hsl(var(--muted-foreground));
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.stat-icon {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  flex-shrink: 0;
}

.stat-icon.blue   { background: rgba(59, 130, 246, 0.15); color: #60a5fa; }
.stat-icon.green  { background: rgba(34, 197, 94, 0.15); color: #4ade80; }
.stat-icon.amber  { background: rgba(245, 158, 11, 0.15); color: #fcd34d; }
.stat-icon.purple { background: rgba(139, 92, 246, 0.15); color: #a78bfa; }
.stat-icon.red    { background: rgba(239, 68, 68, 0.15); color: #f87171; }

.stat-card--clickable { cursor: pointer; }
.stat-card--clickable:hover { box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1); }

.stat-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: hsl(var(--foreground));
  line-height: 1;
  margin-bottom: 0.4rem;
}

.stat-sub {
  font-size: 11px;
  color: hsl(var(--muted-foreground));
  display: flex;
  align-items: center;
  gap: 4px;
}

.trend-up   { color: #22c55e; font-size: 10px; }
.trend-ok   { color: #3b82f6; font-size: 10px; }
.trend-warn { color: #ef4444; font-size: 10px; }

/* ── Charts ── */
.chart-card {
  background: hsl(var(--card));
  border: 1px solid hsl(var(--border));
  border-radius: 12px;
  overflow: hidden;
}

/* ── Modal ── */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 1rem;
}

.modal {
  width: min(560px, 100%);
  max-height: 82vh;
  background: hsl(var(--card));
  border-radius: 14px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.1rem 1.25rem;
  border-bottom: 1px solid hsl(var(--border));
  flex-shrink: 0;
}

.modal-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.modal-title-icon {
  width: 36px;
  height: 36px;
  border-radius: 9px;
  background: hsl(var(--primary) / 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  color: hsl(var(--primary));
  font-size: 15px;
  flex-shrink: 0;
}

.modal-title h3 {
  font-size: 0.95rem;
  font-weight: 700;
  color: hsl(var(--foreground));
  margin: 0 0 2px;
}

.modal-title p {
  font-size: 0.75rem;
  color: hsl(var(--muted-foreground));
  margin: 0;
}

.modal-close {
  width: 30px;
  height: 30px;
  border: none;
  background: hsl(var(--muted));
  border-radius: 7px;
  cursor: pointer;
  color: hsl(var(--muted-foreground));
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  transition: background 0.15s;
  flex-shrink: 0;
}

.modal-close:hover { background: hsl(var(--accent)); }

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 1rem 1.25rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.modal-filters-row {
  display: flex;
  align-items: flex-end;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.modal-filters-row .modal-search-wrap {
  flex: 1 1 200px;
  min-width: 200px;
}

.modal-search-wrap { position: relative; }

.modal-search-icon {
  position: absolute;
  left: 11px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 13px;
  color: hsl(var(--muted-foreground));
  pointer-events: none;
}

.modal-search {
  width: 100%;
  height: 38px;
  background: hsl(var(--background));
  border: 1px solid hsl(var(--border));
  border-radius: 8px;
  padding: 0 12px 0 34px;
  font-size: 13px;
  color: hsl(var(--foreground));
  transition: border-color 0.15s, box-shadow 0.15s;
  box-sizing: border-box;
}

.modal-search:focus {
  outline: none;
  border-color: hsl(var(--primary));
  box-shadow: 0 0 0 3px hsl(var(--primary) / 0.15);
}

.modal-list {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.modal-user-row {
  width: 100%;
  text-align: left;
  border: 1px solid hsl(var(--border));
  background: hsl(var(--card));
  border-radius: 9px;
  padding: 0.65rem 0.85rem;
  display: flex;
  align-items: center;
  gap: 0.85rem;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}

.modal-user-row:hover:not(:disabled) { background: hsl(var(--muted)); border-color: hsl(var(--border)); }
.modal-user-row:disabled { opacity: 0.55; cursor: not-allowed; }

.modal-user-row--disabled {
  opacity: 0.55;
  cursor: not-allowed;
  background: hsl(var(--muted) / 0.4);
  border-style: dashed;
}

.modal-user-row--disabled .modal-user-avatar {
  filter: grayscale(1);
}

.modal-user-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: hsl(var(--primary) / 0.15);
  color: hsl(var(--primary));
  font-size: 13px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.modal-user-info { flex: 1; min-width: 0; }

.modal-user-name {
  margin: 0 0 2px;
  font-weight: 600;
  font-size: 0.85rem;
  color: hsl(var(--foreground));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.modal-user-meta {
  margin: 0;
  font-size: 0.75rem;
  color: hsl(var(--muted-foreground));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.modal-user-badge {
  text-transform: capitalize;
  font-size: 10px;
  font-weight: 700;
  color: hsl(var(--primary));
  background: hsl(var(--primary) / 0.15);
  border-radius: 20px;
  padding: 3px 9px;
  white-space: nowrap;
  flex-shrink: 0;
}

.modal-user-badge--warn {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  text-transform: none;
  color: hsl(var(--foreground));
  background: hsl(var(--foreground) / 0.08);
  border: 1px solid hsl(var(--border));
}

.modal-date-filter {
  display: flex;
  align-items: flex-end;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.modal-date-filter label {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 11px;
  color: hsl(var(--muted-foreground));
  font-weight: 500;
}

.modal-date-input {
  height: 34px;
  background: hsl(var(--background));
  border: 1px solid hsl(var(--border));
  border-radius: 8px;
  padding: 0 10px;
  font-size: 13px;
  color: hsl(var(--foreground));
  box-sizing: border-box;
}

.modal-date-input:focus {
  outline: none;
  border-color: hsl(var(--primary));
  box-shadow: 0 0 0 3px hsl(var(--primary) / 0.15);
}

.modal-date-clear {
  height: 34px;
  padding: 0 12px;
  border: 1px solid hsl(var(--border));
  border-radius: 8px;
  background: hsl(var(--muted));
  color: hsl(var(--muted-foreground));
  font-size: 12px;
  cursor: pointer;
}

.modal-date-clear:hover { background: hsl(var(--accent)); }

.modal-empty {
  font-size: 0.85rem;
  color: hsl(var(--muted-foreground));
  text-align: center;
  padding: 1.5rem 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

/* ────────────────────────────
   RESPONSIVE
──────────────────────────── */

/* Tablet */
@media (max-width: 1200px) {
  .stats-grid { grid-template-columns: repeat(2, 1fr); }
  .stats-grid--3 { grid-template-columns: repeat(3, 1fr); }
}

/* Mobile */
@media (max-width: 767px) {
  /* Show mobile topbar, hide desktop header */
  .mobile-topbar { display: flex; }
  .page-header { display: none; }

  /* Show mobile page header */
  .mobile-page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1rem 0.5rem;
    gap: 0.75rem;
  }

  .mobile-page-header h1 {
    font-size: 1.1rem;
    font-weight: 700;
    color: hsl(var(--foreground));
    margin: 0 0 2px;
  }

  .mobile-page-header p {
    font-size: 0.75rem;
    color: hsl(var(--muted-foreground));
    margin: 0;
  }

  .mobile-scrape-actions {
    display: flex;
    gap: 0.4rem;
    flex-shrink: 0;
  }

  /* On mobile sidebar is overlay — no margin needed */
  .admin-main {
    margin-left: 0 !important;
    max-width: 100% !important;
    padding-top: 56px;
  }

  /* Make modal full screen on mobile */
  .modal-overlay { padding: 0; align-items: flex-end; }
  .modal {
    width: 100%;
    max-height: 90vh;
    border-radius: 18px 18px 0 0;
  }

  /* Tighter content padding */
  .content { padding: 0.85rem 0.85rem 2rem; }
  .banner { margin: 0.75rem 0.85rem 0; }

  /* Stats: 2 cols on mobile */
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.6rem;
  }

  .stat-value { font-size: 1.4rem; }
  .stat-card-inner { padding: 0.75rem 0.85rem 0.85rem; }

  /* Charts: single col */
  .charts-grid { grid-template-columns: 1fr; }
}

@media (max-width: 400px) {
  .stats-grid { grid-template-columns: 1fr; }
}

/* ── Subscription stat icon colour ── */
.stat-icon.indigo { background: rgba(99, 102, 241, 0.15); color: #818cf8; }

/* ── Subscription Overview Card ── */
.subscription-overview-card {
  padding: 0;
}

.subscription-overview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem 0.85rem;
  border-bottom: 1px solid hsl(var(--border));
}

.subscription-overview-title-wrap {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.subscription-overview-icon {
  width: 32px;
  height: 32px;
  border-radius: 9px;
  background: rgba(99, 102, 241, 0.15);
  color: #818cf8;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  flex-shrink: 0;
}

.subscription-overview-title {
  font-size: 0.9rem;
  font-weight: 700;
  color: hsl(var(--foreground));
  margin: 0;
  letter-spacing: -0.01em;
}

.subscription-overview-body {
  padding: 1rem 1.25rem 1.25rem;
}

.sub-overview-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem 1.5rem;
}

.sub-overview-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.6rem 0.75rem;
  background: hsl(var(--muted));
  border: 1px solid hsl(var(--border));
  border-radius: 8px;
}

.sub-overview-key {
  font-size: 0.78rem;
  font-weight: 600;
  color: hsl(var(--muted-foreground));
}

.sub-overview-val {
  font-size: 0.88rem;
  font-weight: 700;
  color: hsl(var(--foreground));
}

.sub-overview-val--warn {
  color: #f87171;
}

@media (max-width: 767px) {
  .sub-overview-grid {
    grid-template-columns: 1fr;
  }
}
</style>